import React, { useEffect, useState } from 'react';
import Icons from './Icons';

/**
 * Top-right notice: message + timeline bar (runs `duration` ms), then slides out to the right.
 */
export default function CornerToast({
  toastKey,
  message,
  onExited,
  duration = 3000,
  variant = 'warning',
}) {
  const [phase, setPhase] = useState('show'); // show | leave

  useEffect(() => {
    if (!toastKey && !message) return;
    setPhase('show');
    const t = setTimeout(() => setPhase('leave'), duration);
    return () => clearTimeout(t);
  }, [toastKey, message, duration]);

  useEffect(() => {
    if (phase !== 'leave') return;
    const t = setTimeout(() => {
      onExited?.();
    }, 320);
    return () => clearTimeout(t);
  }, [phase, onExited]);

  if (!message) return null;

  const styles =
    variant === 'success'
      ? {
          wrap: 'border-green-200 bg-green-50 text-green-950',
          track: 'bg-green-200/60',
          bar: 'bg-green-500',
          icon: 'text-green-600',
          iconName: 'check',
        }
      : variant === 'error'
        ? {
            wrap: 'border-red-200 bg-red-50 text-red-950',
            track: 'bg-red-200/60',
            bar: 'bg-red-500',
            icon: 'text-red-600',
            iconName: 'cross',
          }
        : {
            wrap: 'border-amber-200 bg-amber-50 text-amber-950',
            track: 'bg-amber-200/60',
            bar: 'bg-amber-500',
            icon: 'text-amber-600',
            iconName: 'warning',
          };

  return (
    <div
      role="alert"
      className={`fixed top-4 right-4 z-[10050] w-[min(100vw-2rem,22rem)] pointer-events-none transition-all duration-300 ease-out ${
        phase === 'leave' ? 'translate-x-[calc(100%+2rem)] opacity-0' : 'translate-x-0 opacity-100'
      }`}
    >
      <div
        className={`pointer-events-auto rounded-lg overflow-hidden ring-1 ring-black/5 ${styles.wrap}`}
      >
        <div className="px-4 py-3 flex gap-2.5 items-start">
          <Icons name={styles.iconName} size={18} className={`shrink-0 mt-0.5 ${styles.icon}`} />
          <p className="m-0 text-sm leading-snug">{message}</p>
        </div>
        <div className={`h-1 relative ${styles.track}`}>
          <div
            className={`absolute inset-y-0 left-0 w-full origin-left hawk-toast-progress-bar ${styles.bar}`}
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      </div>
    </div>
  );
}
